import { Box, Card, TextField, Typography } from "@mui/material"
import { Sparkles } from "lucide-react"
import { InlineAiGenerator } from "@crm-src/components/inline-ai-generator"
import { AiTextImprover } from "@crm-src/components/ai-text-improver"
import { BROCHURE_COLORS, XOP_BRAND_MARK_URL, toAbsoluteUrl, type BrochureSection } from "./stock-images"
import { BROCHURE_FONT, ctaButton, escapeHtml } from "./html-helpers"

// Editor UI for the brochure hero: an eyebrow, a big headline, a subheadline,
// and a primary call-to-action (label + URL). The rendered email HTML is
// produced by `heroToHtml` below.
interface HeroSectionProps {
  section: BrochureSection
  onUpdate: (sectionId: string, partial: any) => void
}

export function HeroSection({ section, onUpdate }: HeroSectionProps) {
  const c = section.content
  return (
    <Card sx={{ borderRadius: 0 }} style={{ marginBottom: 0 }}>
      <Box sx={{ p: 3 }}>
        <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Sparkles size={20} />
          Hero
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography component="label" htmlFor={`hero-eyebrow-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Eyebrow</Typography>
            <TextField
              fullWidth size="small" id={`hero-eyebrow-${section.id}`}
              value={c.eyebrow || ""}
              onChange={(e) => onUpdate(section.id, { eyebrow: e.target.value })}
              placeholder="e.g., Introducing"
            />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`hero-headline-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Headline</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="short"
                  context="brochure hero headline — a bold, benefit-led title that grabs attention"
                  placeholder="e.g., unified communications, reimagined"
                  currentValue={c.headline}
                  onGenerate={(v) => onUpdate(section.id, { headline: v })}
                />
                <AiTextImprover fieldType="short" context="brochure hero headline" currentText={c.headline} onImprove={(v) => onUpdate(section.id, { headline: v })} />
              </Box>
            </Box>
            <TextField
              fullWidth size="small" id={`hero-headline-${section.id}`}
              value={c.headline || ""}
              onChange={(e) => onUpdate(section.id, { headline: e.target.value })}
              placeholder="Headline"
            />
          </Box>

          <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography component="label" htmlFor={`hero-sub-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Subheadline</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <InlineAiGenerator
                  fieldType="long"
                  context="brochure hero subheadline — one or two sentences expanding on the headline's promise"
                  placeholder="e.g., describe what you offer"
                  currentValue={c.subheadline}
                  onGenerate={(v) => onUpdate(section.id, { subheadline: v })}
                />
                <AiTextImprover fieldType="long" context="brochure hero subheadline" currentText={c.subheadline} onImprove={(v) => onUpdate(section.id, { subheadline: v })} />
              </Box>
            </Box>
            <TextField
              fullWidth multiline minRows={2} size="small" id={`hero-sub-${section.id}`}
              value={c.subheadline || ""}
              onChange={(e) => onUpdate(section.id, { subheadline: e.target.value })}
              placeholder="Subheadline"
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <TextField
              label="Button label" size="small" InputLabelProps={{ shrink: true }}
              value={c.ctaLabel || ""}
              onChange={(e) => onUpdate(section.id, { ctaLabel: e.target.value })}
              placeholder="e.g., Learn more"
            />
            <TextField
              label="Button link (URL)" size="small" InputLabelProps={{ shrink: true }}
              value={c.ctaUrl || ""}
              onChange={(e) => onUpdate(section.id, { ctaUrl: e.target.value })}
              placeholder="https://xopnetworks.com"
              helperText="Leave blank to hide the button"
            />
          </Box>
        </Box>
      </Box>
    </Card>
  )
}

// Serialize the hero to email HTML: a slim brand strip ([ball] + tenant name),
// then a navy hero band with the eyebrow / headline / subheadline / CTA button.
export function heroToHtml(section: BrochureSection, tenantName: string): string {
  const c = section.content || {}
  const brandMark = toAbsoluteUrl(XOP_BRAND_MARK_URL)
  const eyebrow = c.eyebrow ? escapeHtml(c.eyebrow) : ""
  const headline = escapeHtml(c.headline || "")
  const subheadline = escapeHtml(c.subheadline || "")
  const button = ctaButton(c.ctaUrl || "", c.ctaLabel || "")

  return `
          <!-- Brand strip -->
          <tr>
            <td bgcolor="${BROCHURE_COLORS.navyDeep}" class="mobile-pad" style="background-color: ${BROCHURE_COLORS.navyDeep}; padding: 16px 40px;">
              <table cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr>
                  <td style="vertical-align: middle; padding-right: 12px;" valign="middle">
                    <img src="${brandMark}" alt="${escapeHtml(tenantName)}" width="34" height="34" style="display: block; border: 0; width: 34px; height: 34px;" />
                  </td>
                  <td style="vertical-align: middle;" valign="middle">
                    <span style="color: ${BROCHURE_COLORS.white}; font-family: ${BROCHURE_FONT}; font-size: 16px; font-weight: bold; letter-spacing: 0.3px;">${escapeHtml(tenantName)}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Hero band -->
          <tr>
            <td bgcolor="${BROCHURE_COLORS.navy}" class="mobile-pad" style="background-color: ${BROCHURE_COLORS.navy}; padding: 48px 40px 52px 40px;">
              ${eyebrow ? `
              <p style="margin: 0 0 14px 0; color: ${BROCHURE_COLORS.lime}; font-family: ${BROCHURE_FONT}; font-size: 13px; font-weight: bold; letter-spacing: 2px; text-transform: uppercase;">${eyebrow}</p>
              ` : ''}
              <h1 style="margin: 0 0 16px 0; color: ${BROCHURE_COLORS.white}; font-family: ${BROCHURE_FONT}; font-size: 34px; line-height: 40px; font-weight: bold;">${headline}</h1>
              ${subheadline ? `
              <p style="margin: 0 0 28px 0; color: #C7D0DE; font-family: ${BROCHURE_FONT}; font-size: 17px; line-height: 26px;">${subheadline}</p>
              ` : ''}
              ${button ? `
              <table cellpadding="0" cellspacing="0" border="0" role="presentation">
                <tr><td>${button}</td></tr>
              </table>
              ` : ''}
            </td>
          </tr>
          `
}
